import { useState } from 'react';
import { Formation } from '../../../../shared/types/config';
import configService from '../../../../shared/services/configService';

interface UseFormationConfigParams {
  setError: (msg: string | null) => void;
  setSuccess: (msg: string | null) => void;
  loadAllData: () => Promise<void>;
}

const emptyForm = (): Omit<Formation, 'id' | 'competence_nom'> => ({
  nom: '',
  description: '',
  config_competence_id: null,
  duree_heures: 8,
  prix: 0,
  niveau_personnel_requis: 1,
  ordre: 0,
  actif: true,
});

export function useFormationConfig({
  setError,
  setSuccess,
  loadAllData,
}: UseFormationConfigParams) {
  const [formations, setFormations] = useState<Formation[]>([]);
  const [searchFormation, setSearchFormation] = useState('');
  const [showFormationModal, setShowFormationModal] = useState(false);
  const [editingFormation, setEditingFormation] = useState<Formation | null>(null);
  const [formationForm, setFormationForm] = useState<Omit<Formation, 'id' | 'competence_nom'>>(emptyForm());

  const handleAddFormation = () => {
    setEditingFormation(null);
    setFormationForm(emptyForm());
    setShowFormationModal(true);
  };

  const handleEditFormation = (formation: Formation) => {
    setEditingFormation(formation);
    setFormationForm({
      nom: formation.nom,
      description: formation.description || '',
      config_competence_id: formation.config_competence_id ?? null,
      duree_heures: formation.duree_heures,
      prix: formation.prix,
      niveau_personnel_requis: formation.niveau_personnel_requis,
      ordre: formation.ordre,
      actif: formation.actif,
    });
    setShowFormationModal(true);
  };

  const handleSaveFormation = async () => {
    try {
      setError(null);
      let result;
      if (editingFormation?.id) {
        result = await configService.updateFormation(editingFormation.id, formationForm);
      } else {
        result = await configService.createFormation(formationForm);
      }
      if (result.success) {
        setSuccess(editingFormation ? 'Formation modifiée avec succès' : 'Formation créée avec succès');
        setShowFormationModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteFormation = async (id: number) => {
    if (!window.confirm('Supprimer cette formation ?')) return;
    try {
      setError(null);
      const result = await configService.deleteFormation(id);
      if (result.success) {
        setSuccess('Formation supprimée');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  return {
    formations,
    setFormations,
    searchFormation,
    setSearchFormation,
    showFormationModal,
    setShowFormationModal,
    editingFormation,
    formationForm,
    setFormationForm,
    handleAddFormation,
    handleEditFormation,
    handleSaveFormation,
    handleDeleteFormation,
  };
}
